import "Components/ConfigLayout/Network/MulticastSettings/MulticastSettings.less";
import { type FormEvent, memo, useState } from "react";
import { Card, Input, SettingsLayout, Switch } from "@clintonelec/react-storybook";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import {
	setMulticastSettingsAction, selectMulticastSettings
} from "Data/Redux/Slices/Settings/Network/MulticastSettings";
import { isEqual } from "lodash";
import { ipv4Regex } from "@clintonelec/typescriptutils";
import { produce } from "immer";

const multicastSetupLabels = [
	"Video 1 Stream",
	"Video 2 Stream",
	"Video 3 Stream",
	"Video 4 Stream",
	"Audio Stream",
	"Metadata Stream"
];

function MulticastSettings() {
	const dispatch = useAppDispatch();
	const multicastSettings = useAppSelector(selectMulticastSettings);
	const [ localMulticastSettings, setLocalMulticastSettings ] = useState(multicastSettings);
	const setMulticastSettings = () => dispatch(setMulticastSettingsAction(localMulticastSettings));

	const handleFormReset = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setLocalMulticastSettings(multicastSettings);
	};

	const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setMulticastSettings();
		}
	};

	const handleEnableChange = (index: number) => (checked: boolean) => {
		setLocalMulticastSettings(produce(localMulticastSettings, draft => {
			draft.enable[ index ] = checked;
		}));
	};

	const handleMulticastAddressUpdate = (index: number) => (newMulticastAddress: string) => {
		setLocalMulticastSettings(produce(localMulticastSettings, draft => {
			draft.multicastAddresses[ index ] = newMulticastAddress;
		}));
	};

	const handlePortUpdate = (index: number) => (newPort: string) => {
		setLocalMulticastSettings(produce(localMulticastSettings, draft => {
			draft.port[ index ] = Number(newPort);
		}));
	};

	const handleTtlUpdate = (index: number) => (newTtl: string) => {
		setLocalMulticastSettings(produce(localMulticastSettings, draft => {
			draft.ttl[ index ] = Number(newTtl);
		}));
	};

	const renderSetupLabels = multicastSetupLabels.map((multicastSetupLabel) => {
		return (
			<div key={ multicastSetupLabel }>{ multicastSetupLabel }</div>
		);
	});

	const renderEnable = localMulticastSettings.enable.map((enableSetting, index) => {
		const key = `enable-switch-${ index }`;

		return (
			<Switch checked={ enableSetting } key={ key } onChange={ handleEnableChange(index) } />
		);
	});

	const renderMulticastAddress = localMulticastSettings.multicastAddresses.map((multicastAddress, index) => {
		const key = `multicast-address-input-${ index }`;

		return (
			<Input
				key={ key }
				onSave={ handleMulticastAddressUpdate(index) }
				validator={ ipv4Regex }
				value={ multicastAddress }
			/>
		);
	});

	const renderPort = localMulticastSettings.port.map((port, index) => {
		const key = `port-input-${ index }`;

		return (
			<Input
				hideCharMessage
				key={ key }
				max={ 65535 }
				min={ 0 }
				onSave={ handlePortUpdate(index) }
				type="number"
				value={ port }
			/>
		);
	});

	const renderTtl = localMulticastSettings.ttl.map((ttl, index) => {
		const key = `ttl-input-${ index }`;

		return (
			<Input
				hideCharMessage
				key={ key }
				max={ 255 }
				min={ 1 }
				onSave={ handleTtlUpdate(index) }
				type="number"
				value={ ttl }
			/>
		);
	});

	return (
		<SettingsLayout.Form
			className="multicast-form"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="Multicast Setup">
					<div className="setup">
						<div className="setup-column">
							<div>&nbsp;</div>
							<div className="setup-label">
								{ renderSetupLabels }
							</div>
						</div>
						<div className="setup-column">
							<div>Enable</div>
							<div className="setup-label">
								{ renderEnable }
							</div>
						</div>
						<div className="setup-column">
							<div>Multicast Address</div>
							{ renderMulticastAddress }
						</div>
						<div className="setup-column">
							<div>Port</div>
							{ renderPort }
						</div>
						<div className="setup-column">
							<div>TTL</div>
							{ renderTtl }
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(localMulticastSettings, multicastSettings) } />
		</SettingsLayout.Form>
	);
}

export default memo(MulticastSettings);
